import React, {useEffect, useMemo} from "react";
import {Button, Colors, IconEdit, LoadingButton, ToggleButton, ToggleButtonGroup} from "bounce-ui/backoffice";
import {AccountExternal} from "declerations/server/account_models";
import {FirstPlan} from "../../../../../../../../declerations/server/payment_plan_models";
import {DEFAULT_VALUES, getFirstPlan, HardshipAmountType, PlanType, planTypeToInstallments} from "../utils";
import {KeyValueRow, StyledCard} from "../Components";
import ConfigureDownPayment from "../configure-plan/ConfigureDownPayment";
import ConfigureHardshipPlan from "../configure-plan/ConfigureHardshipPlan";

interface IChooseInstallmentsAmount {
  defaultValues: Record<string, any>;
  userBalance: AccountExternal;
  fetchPaymentOffers: ({firstPlan, planType}: {firstPlan: FirstPlan; planType: PlanType}) => Promise<void>;
  setResetSelectedPlan: (value: boolean) => void;
  isLoadingData: boolean;
  editMode: boolean;
  setEditMode: (value: boolean) => void;
  planType: PlanType | undefined;
  setPlanType: (planType: PlanType) => void;
  noPlanOffers: boolean;
}

const ConfigurePlan = ({
  defaultValues,
  userBalance,
  fetchPaymentOffers,
  setResetSelectedPlan,
  isLoadingData,
  editMode,
  setEditMode,
  planType,
  setPlanType,
  noPlanOffers,
}: IChooseInstallmentsAmount) => {
  const [downPayment, setDownPayment] = React.useState<number | undefined>(defaultValues?.downPayment);
  const [numberOfHardshipInstallments, setNumberOfHardshipInstallments] = React.useState<number | undefined>(
    defaultValues?.numberOfHardshipInstallments
  );
  const [hardshipAmount, setHardshipAmount] = React.useState<HardshipAmountType | undefined>(
    defaultValues?.hardshipAmount
  );
  const [hardshipCustomAmount, setHardshipCustomAmount] = React.useState<number | undefined>(
    defaultValues?.hardshipCustomAmount
  );
  const [error, setError] = React.useState<string>();

  const fetchOffersByPlanType = async (inputPlanType?: PlanType) => {
    const firstPlan = getFirstPlan({
      planType: inputPlanType,
      downPayment,
      numberOfHardshipInstallments,
      hardshipAmount,
      hardshipCustomAmount,
    });
    await fetchPaymentOffers({firstPlan, planType: inputPlanType || PlanType.MATRIX_NORMAL});
  };

  const handleContinue = async (inputPlanType: PlanType) => {
    await fetchOffersByPlanType(inputPlanType);
    setEditMode(false);
  };

  const disableContinue = useMemo(() => {
    return (
      !editMode ||
      (hardshipAmount === "custom" && !hardshipCustomAmount) ||
      (planType === PlanType.MATRIX_DOWN_PAYMENT && !downPayment) ||
      noPlanOffers
    );
  }, [editMode, planType, hardshipAmount, hardshipCustomAmount, downPayment, noPlanOffers]);

  const handleChange = (event: React.MouseEvent<HTMLElement>, value: PlanType) => {
    if (value) {
      setPlanType(value);
      if (value === PlanType.MATRIX_NORMAL) {
        handleContinue(value);
      }
      setResetSelectedPlan(true);
      setNumberOfHardshipInstallments(planTypeToInstallments[value] ?? 0);
      if (value === PlanType.MATRIX_DOWN_PAYMENT) {
        setDownPayment(DEFAULT_VALUES.downPaymentInstallment);
      }
      if (value === PlanType.MATRIX_HARDSHIP) {
        setHardshipAmount(DEFAULT_VALUES.hardshipInstallment);
      }
    }
  };

  useEffect(() => {
    fetchOffersByPlanType(planType);
    setError("");
  }, [planType]);

  return (
    <StyledCard>
      <KeyValueRow field={"Plan structure"}>
        <ToggleButtonGroup
          exclusive
          value={planType}
          defaultValue={"normal"}
          onChange={handleChange}
          disabled={!editMode}
        >
          <ToggleButton value={PlanType.MATRIX_NORMAL}>Regular</ToggleButton>
          <ToggleButton value={PlanType.MATRIX_DOWN_PAYMENT}>Down payment</ToggleButton>
          <ToggleButton value={PlanType.MATRIX_HARDSHIP}>Hardship</ToggleButton>
        </ToggleButtonGroup>
      </KeyValueRow>
      {planType === PlanType.MATRIX_DOWN_PAYMENT && (
        <ConfigureDownPayment
          downPayment={downPayment}
          setDownPayment={setDownPayment}
          editMode={editMode}
          error={error}
          setError={setError}
          totalBalance={userBalance.total_balance}
        />
      )}
      {planType === PlanType.MATRIX_HARDSHIP && (
        <ConfigureHardshipPlan
          hardshipAmount={hardshipAmount}
          setHardshipAmount={setHardshipAmount}
          numberOfHardshipInstallments={numberOfHardshipInstallments}
          setNumberOfHardshipInstallments={setNumberOfHardshipInstallments}
          setHardshipCustomAmount={setHardshipCustomAmount}
          totalBalance={userBalance.total_balance}
          error={error}
          setError={setError}
          hardshipCustomAmount={hardshipCustomAmount}
          editMode={editMode}
        />
      )}
      {editMode ? (
        <LoadingButton
          id={"Continue"}
          fullWidth
          onClick={() => handleContinue(planType || PlanType.MATRIX_NORMAL)}
          disabled={disableContinue || (hardshipAmount === "custom" && !!error)}
          loading={isLoadingData}
          size={"medium"}
        />
      ) : (
        <Button
          buttonType={"contained-secondary"}
          icon={<IconEdit color={Colors.primary._700} />}
          fullWidth
          onClick={() => setEditMode(true)}
          size={"medium"}
        >
          Edit
        </Button>
      )}
    </StyledCard>
  );
};

export default ConfigurePlan;
